import { useMemo, useRef } from 'react';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { selectClipData } from 'components/app/_helpers/_app.helper';
import { selectFileClips, setActiveClip } from 'components/app/editor/_mediator/editor.slice';

type Payload = { breakPoints: Seconds[]; clips: (ClipId | null)[] };
const RANGE = { min: 0, max: 0 };
const INFINITY = 100000;

export const usePlayerSelect = () => {
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const clips = useAppSelector(selectFileClips);
  const range = useRef(RANGE);
  const dispatch = useAppDispatch();

  const payload = useMemo(() => {
    if (!clips?.length || !clipsMap) return;
    const payload: Payload = { breakPoints: [], clips: [] };
    let prevEndTime: Seconds | boolean = true;
    clips?.forEach((clipId) => {
      const clip = selectClipData(clipId, clipsMap);
      if (clip.startTime !== null && clip.endTime !== null) {
        if (prevEndTime === clip.startTime) {
          payload.clips.push(clip.clipId);
          payload.breakPoints.push(clip.endTime);
        } else {
          if (prevEndTime) payload.clips.push(null);
          payload.breakPoints.push(clip.startTime);
          payload.clips.push(clip.clipId);
          payload.breakPoints.push(clip.endTime);
        }
        prevEndTime = clip.endTime;
      } else if (clip.startTime !== null) {
        if (prevEndTime === clip.startTime) payload.clips.push(clip.clipId);
        else {
          if (prevEndTime) payload.clips.push(null);
          payload.breakPoints.push(clip.startTime);
          payload.clips.push(clip.clipId);
        }
        prevEndTime = false;
      } else {
        if (prevEndTime) payload.clips.push(clip.clipId);
        payload.breakPoints.push(clip.endTime!);
        prevEndTime = clip.endTime!;
      }
    });
    const lastClip = selectClipData(clips[clips.length - 1], clipsMap);
    if (lastClip.endTime !== null) payload.clips.push(null);
    payload.breakPoints.push(INFINITY);
    range.current = RANGE;
    return payload;
  }, [clips]);

  return (time: Seconds) => {
    if (!payload) return;
    if (time >= range.current.min && time < range.current.max) return;
    const index = payload.breakPoints.findIndex((breakPoint, i) => {
      if (time >= breakPoint) return false;
      range.current = { min: i === 0 ? 0 : payload.breakPoints[i - 1], max: payload.breakPoints[i] };
      return true;
    });
    dispatch(setActiveClip(index === -1 ? null : payload.clips[index]));
  };
};
